// src/app/shared/name-change-requests/NameChangeRequestsTable.tsx
"use client";

import React, { useEffect } from "react";
import Table from "@core/components/table";
import TablePagination from "@core/components/table/pagination";
import TableFooter from "@core/components/table/footer";
import { useTanStackTable } from "@core/components/table/custom/use-TanStack-Table";
import Filters from "./filters";
import { exportToCSV } from "@core/utils/export-to-csv";
import { NameChangeColumns } from "./columns";
import type { NameChangeDataInterface } from "../../../data/name-change";

interface NameChangeRequestsTableProps {
  requests: NameChangeDataInterface[];
  isLoading: boolean;
  error: Error | null;
}

export default function NameChangeRequestsTable({
  requests,
  isLoading,
  error,
}: NameChangeRequestsTableProps) {
  if (isLoading) {
    return (
      <div className="w-full h-60 flex justify-center items-center font-semibold">
        Loading name-change requests…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-red-600 p-4">
        Error loading requests: {error.message}
      </div>
    );
  }

  const { table, setData } = useTanStackTable<NameChangeDataInterface>({
    tableData: requests,
    columnConfig: NameChangeColumns,
    options: {
      initialState: { pagination: { pageIndex: 0, pageSize: 5 } },
      meta: {
        updateRow: (index, patch) =>
          setData((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], ...patch };
            return copy;
          }),
      },
      enableColumnResizing: false,
    },
  });

  const selected = table.getSelectedRowModel().rows.map((r) => r.original);

  const handleExport = () => {
    exportToCSV(
      selected,
      "RequestID,CurrentName,RequestedName,Reason,DateRequested,Status",
      `name_changes_${selected.length}`
    );
  };

  useEffect(() => {
    table.setColumnOrder([
      "id",
      "currentName",
      "requestedName",
      "reason",
      "dateRequested",
      "status",
      "actions",
    ]);
  }, [table]);

  return (
    <>
      <Filters table={table} />
      <Table
        table={table}
        variant="modern"
        classNames={{ container: "border border-muted rounded-md" }}
      />
      <TableFooter table={table} onExport={handleExport} />
      <TablePagination table={table} className="py-4" />
    </>
  );
}
